import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Sun, Sunrise, Sunset, Moon, Pencil, Check, User, X, Bell, RefreshCw, Send,
} from 'lucide-react';
import ClientSectionHeader from '../components/client/ClientSectionHeader';
import PatientQuestionnaireWizard from '../components/patient/PatientQuestionnaireWizard';
import MisPautasPaciente from '../components/admin/fisiovision/paciente/MisPautasPaciente';
import MiMotivacionPaciente from '../components/admin/fisiovision/paciente/MiMotivacionPaciente';
import verHojaHTML from '../components/admin/fisiovision/paciente/hojaEntenderHTML';
import pintarPendientesHTML from '../components/admin/fisiovision/paciente/cuestionariosHTML';
import sf12HTML from '../components/admin/fisiovision/paciente/sf12HTML';
import odiHTML from '../components/admin/fisiovision/paciente/odiHTML';
import resultadoHTML from '../components/admin/fisiovision/paciente/resultadoHTML';
// El MISMO triangulito de play que usa Canal Vídeos (icono corporativo, no emoji).
import * as I from '../components/admin/canal/vcIcons';
// Gráfica OFICIAL de evolución del EVA/VAS, la MISMA que usa la ficha clínica. Se
// reutiliza tal cual (no se toca: es compartida con la vista del fisio) y ya trae la
// animación de la línea. Norma de Ann: los cuestionarios son oficiales validados.
import VasEvolucionClinica from '../components/admin/clinica/patient/VasEvolucionClinica';
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from 'recharts';
import { API } from '../api';
import '../styles/areas/clinica-portal.css';
// EL CLON de FisioVisión · el CSS entero del prototipo, acotado a `.fvpac`.
// Va ANTES del CSS propio del CRM para que los ajustes del CRM puedan
// matizarlo, pero el diseño base es el del prototipo, sin retocar.
import '../components/admin/fisiovision/paciente/fisiovision-clon.css';
import '../components/admin/fisiovision/paciente/espacio-paciente.css';

// ── FisioVisión · «Mi fisio y yo» (diseño nuevo rescatado de EspacioPacienteFisio) ──
const fvSvg = (paths, size = 24, sw = 1.9) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: paths }} />
);

// Normaliza la URL del vídeo del ejercicio a una URL reproducible (igual que PatientDashboard):
// video_url puede venir como URL completa, /api/uploads/…, /uploads/… o ruta suelta.
// Sin esto, el <video src> en crudo puede dar 404 y no reproducir lo configurado.
// Al terminar un vídeo, el navegador deja congelado el ÚLTIMO fotograma (normalmente
// negro) y la carátula no vuelve a salir: la `poster` solo se pinta ANTES de la primera
// reproducción. Esto lo devuelve al principio y fuerza a repintar la carátula, así el
// bloque queda igual de bonito que antes de darle al play (Ann, 22-jul-2026).
const volverALaCaratula = (e) => {
  const v = e?.currentTarget;
  if (!v) return;
  try {
    v.pause();
    v.currentTime = 0;
    v.load();   // sin esto Safari se queda con el último fotograma pintado
  } catch (err) { /* si el navegador no deja, se queda como estaba: nunca rompe */ }
};

const getVideoUrl = (url) => {
  if (!url) return null;
  // Red de seguridad: por el CRM circulan FICHAS de vídeo ({ video_id, url, titulo,
  // thumbnail }) además de direcciones sueltas. Si aquí entra una ficha, se saca la
  // dirección en vez de reventar con "url.startsWith is not a function" y llevarse
  // por delante media pantalla del paciente (pasó con el vídeo de bienvenida).
  if (typeof url === 'object') url = url.url || url.video_url || '';
  if (typeof url !== 'string' || !url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API}${url.substring(4)}`;
  if (url.startsWith('/uploads/')) return `${API}${url}`;
  return `${API}${url.startsWith('/') ? url : '/' + url}`;
};

/**
 * EspacioPacienteClinica — Vista del paciente al entrar a «Clínica MEFIR».
 *
 * Contenido ENLAZADO al CRM (mismo diseño): /api/paciente/mi-espacio (fisio, episodio,
 * mensaje del fisio, pautas activas con grupos, cuestionarios pendientes) +
 * /api/paciente/cuestionarios/completados. NO se cambia el diseño, solo los datos.
 */

const SECCIONES = ['mifisio', 'pautas', 'cuestionarios', 'evolucion'];

const token = () => localStorage.getItem('token');

// dd/mm/yyyy a partir de un ISO/fecha string (vacío si no hay).
const fmt = (iso) => {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  return (d && m && y) ? `${d}/${m}/${y}` : s;
};

// Hora local (HH:MM) de un ISO, para los mensajes del chat.
const fmtHora = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch (e) { return ''; }
};

// Nombre OFICIAL de cada cuestionario (mismo catálogo que la configuración clínica).
// Sin esto el paciente veía el identificador en crudo ("vas_oficial").
// Nombre TÉCNICO oficial de cada cuestionario. Se conserva porque es el que usa la
// ficha clínica y el que hay que poder consultar; al paciente NO se le enseña.
const NOMBRE_CUESTIONARIO = {
  vas_oficial: 'EVA / VAS dolor (0-100 mm)',
  eq5d5l: 'Calidad de vida (versión simple)',
  psfs: 'PSFS (Escala Funcional Específica)',
  srs22r: 'SRS-22r (Escoliosis)',
  saq: 'SAQ (Apariencia Escoliosis)',
  sf12: 'SF-12 (Calidad de Vida)',
  dolor_mefir: 'Escala de Dolor MEFIR',
  odi: 'Oswestry (ODI)',
  efp0: 'Estado Funcional (EFP-0)',
};

// Nombre FÁCIL, el que ve la paciente (Ann: nada de tecnicismos). El cuestionario en
// sí no se toca — son oficiales validados —, solo cómo se llama en su pantalla.
const NOMBRE_FACIL = {
  vas_oficial: 'Test de dolor',
  dolor_mefir: 'Test de dolor por zonas',
  psfs: 'Test de tus actividades del día a día',
  eq5d5l: 'Test de cómo te encuentras',
  sf12: 'Test de cómo te encuentras',
  srs22r: 'Test de espalda',
  saq: 'Test de tu espalda',
  odi: 'Test de espalda y vida diaria',
  efp0: 'Test de movilidad',
};

const nombreCuestionario = (q) => NOMBRE_FACIL[q?.tipo] || NOMBRE_CUESTIONARIO[q?.tipo] || q?.nombre || 'Test';
// El nombre oficial va en el `title`, por si hace falta consultarlo.
const nombreOficial = (q) => NOMBRE_CUESTIONARIO[q?.tipo] || q?.tipo || '';

// Tamaños del título/subtítulo de la cabecera, con las MISMAS claves que usa el
// editor del backend (xs|sm|md|lg|xl), para que se vea lo que se diseña allí.
// MISMA escala que el banner del canal (components/admin/canal/AnuncioBanner.jsx).
// Antes aquí había otra distinta (lg valía 21 en vez de 26), así que elegías un
// tamaño en el editor y en el espacio del paciente se veía OTRO: parecía que el
// editor no servía para nada (Ann, 22-jul-2026). Una sola escala para los dos sitios:
// lo que se elige es lo que se ve, aquí y en el canal.
const TAMANO = { xs: 13, sm: 16, md: 20, lg: 26, xl: 32 };

// Icono del momento del día. Son EXACTAMENTE los mismos que usa la fisio en el
// administrador (PautaBuilder: MOMENTOS_DIA), para que lo que ella monta y lo que
// ve la paciente se parezcan (Ann). Ojo: allí "mediodía" es Sun y "mañana" Sunrise.
const iconoMomento = (txt) => {
  const t = (txt || '').toLowerCase().replace(/[áàä]/g, 'a').replace(/[íì]/g, 'i');
  if (t.includes('noche') || t.includes('dormir') || t.includes('acostar')) return <Moon size={16} />;
  if (t.includes('tarde')) return <Sunset size={16} />;
  if (t.includes('mediodia') || t.includes('medio dia')) return <Sun size={16} />;
  if (t.includes('manana') || t.includes('levantar')) return <Sunrise size={16} />;
  return <Sun size={16} />;
};

// El marcado de ejercicios dejó de ser un deslizador por ejercicio (Ann, 23-jul-2026):
// una pauta es un HÁBITO diario, así que ahora se marca por MOMENTO en "Mi calendario
// de pautas". El antiguo componente DeslizadorHecho se retiró.

// Puntuación que marcó el PACIENTE en un cuestionario completado, con su escala
// oficial. Norma de Ann: los cuestionarios son oficiales validados, aquí NO se
// calcula ni se interpreta nada — solo se enseña el valor tal y como se guardó.
// Si un tipo no tiene un número claro, se devuelve null y no se pinta nada.
// Escala oficial de cada cuestionario, solo para poder escribir "47 / 100".
const ESCALA_CUESTIONARIO = {
  vas_oficial: { max: 100 },   // EVA / VAS 0-100 mm
  dolor_mefir: { max: 10 },    // Escala de Dolor MEFIR 0-10
};
const puntuacionCuestionario = (q) => {
  // El valor viene ya resuelto del servidor (mira en todos los campos donde el CRM
  // ha ido guardando el EVA a lo largo del tiempo), igual que la pantalla del fisio.
  const esc = ESCALA_CUESTIONARIO[q?.tipo];
  const bruto = typeof q?.valor === 'number' ? q.valor : null;
  if (bruto === null || Number.isNaN(bruto)) return null;
  const n = Number.isInteger(bruto) ? String(bruto) : String(bruto).replace('.', ',');
  return esc ? `${n} / ${esc.max}` : n;
};

// Etiqueta de evolución para un cuestionario ya respondido, comparándolo con el
// ANTERIOR del mismo tipo. Norma de Ann: el paciente NUNCA lee "has empeorado" ni
// nada que desmotive; lo peor que puede leer es "todavía no se aprecia mejoría".
// El umbral de "significativa" es el MCID de la escala (13 mm en el EVA 0-100, el
// mismo que usa la gráfica clínica oficial); se escala si el máximo es otro.
const etiquetaEvolucion = (valor, valorAnterior, max) => {
  if (typeof valor !== 'number' || typeof valorAnterior !== 'number') return null;
  const significativo = (max || 100) * 0.13;   // MCID
  const ruido = significativo / 4;             // cambio demasiado pequeño para contar
  const delta = valor - valorAnterior;         // en dolor, BAJAR es mejorar
  if (delta <= -significativo) return { txt: 'Mejoría significativa', color: '#0f7a5a', bg: '#e4f6ec' };
  if (delta < -ruido)          return { txt: 'Mejoría',               color: '#0f7a5a', bg: '#e9f7f0' };
  if (delta <= ruido)          return { txt: 'Estabilizado',          color: '#2C6675', bg: '#e9f2f5' };
  return { txt: 'Todavía no se aprecia mejoría', color: '#8a6a2e', bg: '#fbf3e2' };
};

// Comentario del Canal: nace de CRUZAR dos cosas reales — que el dolor haya bajado
// y que el paciente haya sido constante con sus rutinas. Si no ha marcado ningún
// día de ejercicios, NO se dice nada de constancia: no se felicita a nadie por algo
// que no ha hecho. Las frases van por índice (no al azar) para que no bailen.
const FRASES_CONSTANCIA = [
  'Tu constancia está dando sus frutos.',
  'Gran trabajo: tu rutina se está notando.',
  'Has cuidado tus ejercicios y tu cuerpo lo agradece.',
  'La disciplina de estas semanas se refleja aquí.',
];
const comentarioDelCanal = (etiqueta, diasConEjercicios, indice) => {
  if (!etiqueta) return null;
  const mejora = etiqueta.txt === 'Mejoría significativa' || etiqueta.txt === 'Mejoría';
  if (mejora && diasConEjercicios >= 3) {
    return FRASES_CONSTANCIA[indice % FRASES_CONSTANCIA.length];
  }
  if (mejora) return 'Vas bien. Sigue así.';
  if (etiqueta.txt === 'Estabilizado') return 'Mantenerse también cuenta. Sigue con tus ejercicios.';
  return 'Cada día que haces tus ejercicios cuenta. Cuéntaselo a tu fisio.';
};

// "jueves 24 de julio" a partir de 'YYYY-MM-DD'. Si la cita es hoy o mañana se dice
// así, que es como lo diría cualquiera y se entiende a la primera.
const fechaSesion = (iso) => {
  if (!iso) return '';
  try {
    const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    const f = new Date(a, m - 1, d);
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const dias = Math.round((f - hoy) / 86400000);
    if (dias === 0) return 'Hoy';
    if (dias === 1) return 'Mañana';
    const t = f.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    return t.charAt(0).toUpperCase() + t.slice(1);
  } catch (e) { return iso; }
};

// Mensaje motivador del calendario (Ann): SIEMPRE en positivo — rigor, disciplina,
// cuidado de la salud, empoderamiento, ánimo… Nunca una regañina. Si lleva días sin
// completar, se anima con cariño ("empezar es lo más importante"), no se culpabiliza.
const FRASES_LOGRO = [
  '¡Bravo! Rutina completa. Tu cuerpo te lo agradece.',
  'Gran disciplina. Cada día que cumples, avanzas.',
  'Tu constancia es tu mejor medicina.',
  '¡Qué implicación! Cuidarte así es quererte.',
  'Rigor y cariño por tu salud. Se nota.',
  '¡Sigue así! Estás tomando las riendas de tu recuperación.',
  'Un día más hecho. Pequeños pasos, gran mejora.',
];
const mensajeMotivador = (cal) => {
  if (!cal || !cal.dias) return null;
  const programadoEse = (fecha) => cal.celdas.filter((c) => c.fecha === fecha && c.programado);
  const completo = (fecha) => { const cs = programadoEse(fecha); return cs.length > 0 && cs.every((c) => c.hecho); };
  const algoHecho = (fecha) => programadoEse(fecha).some((c) => c.hecho);
  // Días con pauta programada, de hoy hacia atrás (sin contar el futuro).
  const pasados = cal.dias.filter((d) => !d.es_futuro && programadoEse(d.fecha).length > 0);
  if (pasados.length === 0) return { txt: 'Cuando hagas tus ejercicios, márcalos aquí. ¡Cada uno cuenta!', tono: 'neutro' };
  const hoyObj = pasados[pasados.length - 1];
  // Racha de días COMPLETOS consecutivos (terminando en el último día pasado).
  let racha = 0;
  for (let i = pasados.length - 1; i >= 0; i--) { if (completo(pasados[i].fecha)) racha++; else break; }
  // Días seguidos SIN completar (excluyendo hoy si aún está a medias, no fallado).
  let sinCompletar = 0;
  for (let i = pasados.length - 1; i >= 0; i--) {
    if (completo(pasados[i].fecha)) break;
    // "hoy" sin terminar aún no cuenta como día perdido
    if (pasados[i].fecha === hoyObj.fecha && !algoHecho(hoyObj.fecha)) { continue; }
    if (pasados[i].fecha === hoyObj.fecha) { break; }
    sinCompletar++;
  }
  // Hoy ya completo → felicitación variada (índice estable por nº de días de racha).
  if (completo(hoyObj.fecha)) {
    if (racha >= 3) return { txt: `¡Llevas ${racha} días seguidos! Tu constancia se está notando muchísimo.`, tono: 'logro' };
    return { txt: FRASES_LOGRO[racha % FRASES_LOGRO.length], tono: 'logro' };
  }
  // Sin completar días → ánimo, nunca reproche.
  if (sinCompletar >= 2) return { txt: `Llevas ${sinCompletar} días sin completar la rutina. Sin presión: retomarla hoy ya es un gran paso. ¡Ánimo!`, tono: 'animo' };
  if (sinCompletar === 1) return { txt: 'Ánimo, ayer no completaste tu rutina. Empezar es lo más importante: hoy es tu día.', tono: 'animo' };
  if (algoHecho(hoyObj.fecha)) return { txt: '¡Bien! Ya has empezado hoy. Termina el resto cuando puedas.', tono: 'logro' };
  return { txt: 'Hoy es un buen día para tu rutina. ¡Tú puedes!', tono: 'neutro' };
};

const enunciadoMomento = (m) => {
  const t = (m || '').toLowerCase().replace(/[áà]/g, 'a').replace(/í/g, 'i');
  if (t.includes('noche') || t.includes('dormir') || t.includes('acostar')) return 'Al dormir';
  if (t.includes('tarde')) return 'Por la tarde';
  if (t.includes('mediodia') || t.includes('medio dia')) return 'A mediodía';
  if (t.includes('manana') || t.includes('levantar')) return 'Al levantarte';
  return m || '';
};

// Línea de progresión animada (Ann: que se mueva y llame la atención). Es acumulada:
// SIEMPRE sube, nunca baja, así los días flojos no castigan. Se dibuja sola al entrar.
const LineaProgresion = ({ puntos, alto = 150 }) => {
  if (!puntos || puntos.length === 0) return null;
  const hayAlgo = puntos.some((p) => p.valor > 0);
  if (!hayAlgo) {
    return <div style={{ height: alto, display: 'flex', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', color: '#5a6b72', fontSize: 13, padding: 8 }}>
      En cuanto marques tus ejercicios, verás tu línea subir.
    </div>;
  }
  return (
    <div style={{ width: '100%', height: alto }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={puntos} margin={{ top: 8, right: 8, bottom: 0, left: -26 }}>
          <defs>
            <linearGradient id="gradProg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e9e6a" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#1e9e6a" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis dataKey="etiqueta" tick={{ fontSize: 10, fill: '#8a97a0' }} axisLine={false} tickLine={false}
                 interval="preserveStartEnd" minTickGap={12} />
          <Tooltip cursor={{ stroke: '#1e9e6a', strokeWidth: 1 }}
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
            formatter={(v) => [`${v} ejercicio${v === 1 ? '' : 's'}`, 'Llevas']} />
          <Area type="monotone" dataKey="valor" stroke="#1e9e6a" strokeWidth={3}
            fill="url(#gradProg)" isAnimationActive animationDuration={1200}
            dot={false} activeDot={{ r: 5, fill: '#1e9e6a' }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};


// ── LO QUE EL PACIENTE VE DE SU PAUTA · FORMATO NUEVO ──────────────────────
// El fisio diseña por TRAMOS (rangos de días) → BLOQUES (días de la semana +
// momento del día) → ejercicios. El paciente no tiene que saber nada de eso: se
// le enseña lo que le toca HOY. La fuente es SIEMPRE `periodos`; el formato
// viejo solo queda para las pautas antiguas, que no se van a rehacer.
const DIA_SEMANA = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];

const MOMENTO_TITULO = {
  sin_momento: '', al_levantarse: 'Al levantarse', manana: 'Por la mañana',
  mediodia: 'A mediodía', tarde: 'Por la tarde', noche: 'Por la noche',
  antes_de_acostarse: 'Antes de acostarse', hora_concreta: '',
};
const gruposDelDia = (pauta, fechaRef) => {
  const periodos = pauta?.periodos || [];
  if (!periodos.length) return null;

  const hoy = fechaRef ? new Date(`${fechaRef}T00:00:00`) : new Date();
  const inicio = pauta.fecha_inicio ? new Date(`${String(pauta.fecha_inicio).slice(0, 10)}T00:00:00`) : hoy;
  // Día 1 = el día que empieza la pauta.
  const diaDelPlan = Math.floor((hoy - inicio) / 86400000) + 1;
  const tramo = periodos.find((x) => diaDelPlan >= x.desde_dia && diaDelPlan <= x.hasta_dia) || periodos[0];
  const diaSemana = DIA_SEMANA[hoy.getDay()];

  const grupos = [];
  (tramo.bloques || []).forEach((b) => {
    // Sin días marcados = todos los días.
    const tocaHoy = !(b.dias || []).length || (b.dias || []).includes(diaSemana);
    if (!tocaHoy) return;
    (b.momentos || []).forEach((m) => {
      const ejercicios = (m.items || []).map((ej) => ({
        nm: ej.nombre || 'Ejercicio',
        dosis: [
          ej.series ? `${ej.series} series` : null,
          ej.repeticiones ? `${ej.repeticiones} reps` : null,
          ej.descanso ? `descanso ${ej.descanso}` : null,
        ].filter(Boolean),
        nota: ej.indicacion || '',
        thumbnail: ej.ilustracion_url || ej.miniatura_url || '',
        video_url: ej.video_url || '',
      }));
      if (!ejercicios.length) return;
      grupos.push({
        momentoDia: m.momento || '',
        titulo: MOMENTO_TITULO[m.momento] || '',
        hora: m.momento === 'hora_concreta' ? (m.hora || '') : '',
        nota: m.nota || '',
        ejercicios,
      });
    });
  });
  return grupos;
};

const EspacioPacienteClinica = ({ user, onLogout, onBackToSelector }) => {
  const [tab, setTab] = useState('mifisio');

  // Mi motivación (reto del paciente) · origen canónico: GET /api/paciente/mi-reto (4e).
  // null si el paciente no tiene reto activo → la pestaña «Mi motivación» NO se muestra.
  const [reto, setReto] = useState(null);
  useEffect(() => {
    fetch('/api/paciente/mi-reto', { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setReto(d && d.reto_nombre ? d : null))
      .catch(() => setReto(null));
  }, []);

  // Hojas «Entender» (educativas) que el fisio ha ASIGNADO a este paciente.
  // Origen: GET /api/paciente/mis-hojas-entender (F2/IDOR: el paciente sale del
  // token, nunca de un parámetro). Solo llegan las PUBLICADAS. Si no hay ninguna,
  // los botones «Entender mi…» no se pintan (dato real o no se pinta).
  const [hojasEntender, setHojasEntender] = useState([]);
  useEffect(() => {
    fetch('/api/paciente/mis-hojas-entender', { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setHojasEntender(Array.isArray(d) ? d : []))
      .catch(() => setHojasEntender([]));
  }, []);

  // EL CATÁLOGO de «Entender» · TODAS las hojas publicadas, no solo las suyas.
  // Origen: GET /api/paciente/hojas-entender-catalogo → { hojas, zonas, total },
  // con `asignada` marcando las que le ha enviado su fisio.
  //
  // ⚠️ Es OTRO endpoint que el de arriba, y hace falta: con `mis-hojas-entender`
  // (solo las suyas) NO se puede tener el menú de zonas, ni la cuenta, ni la
  // marca «te la ha enviado» — porque todas serían suyas y no hay con qué
  // contrastar. El de arriba se queda porque la puerta de «Mi fisio y yo» sí
  // quiere SOLO las suyas.
  const [catalogo, setCatalogo] = useState({ hojas: [], zonas: [], total: 0 });
  const [catalogoCargado, setCatalogoCargado] = useState(false);
  useEffect(() => {
    fetch('/api/paciente/hojas-entender-catalogo', { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Array.isArray(d.hojas)) setCatalogo(d); })
      .catch(() => { /* se queda vacío · nunca datos inventados */ })
      .finally(() => setCatalogoCargado(true));
  }, []);
  // Zona elegida en el menú. 'todas' = sin filtrar.
  const [zonaVista, setZonaVista] = useState('todas');
  // La hoja abierta del listado.
  //
  // ⚠️ Es una variable DISTINTA de «la que le asignó su fisio», y tiene que
  // seguir siéndolo: si se mezclan, el paciente entra a curiosear una hoja y el
  // sistema pasa a creer que su fisio se la mandó — y la etiqueta «te la ha
  // enviado» empieza a mentir en la pantalla de alguien que se fía.
  const [hojaAbierta, setHojaAbierta] = useState(null);
  // El CUERPO de la hoja abierta (GET /api/paciente/hoja-entender/{id}).
  // El listado solo trae los campos mínimos; el texto se pide al abrirla.
  const [hojaCuerpo, setHojaCuerpo] = useState(null);
  // Su pauta REAL · la hoja no lleva lista de ejercicios: lleva un PORQUÉ por
  // ejercicio, y el render recorre la pauta del paciente. Si su fisio no se lo
  // ha mandado, no lo ve.
  const [pautaHoja, setPautaHoja] = useState({ ejercicios: [], pasos: {} });
  useEffect(() => {
    fetch('/api/paciente/mis-pautas-fisiovision', { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Array.isArray(d.ejercicios)) setPautaHoja(d); })
      .catch(() => { /* sin pauta → el bloque no se pinta */ });
  }, []);
  const [cargandoHoja, setCargandoHoja] = useState(false);
  useEffect(() => {
    if (!hojaAbierta) { setHojaCuerpo(null); return; }
    setCargandoHoja(true); setHojaCuerpo(null);
    fetch(`/api/paciente/hoja-entender/${hojaAbierta}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setHojaCuerpo(d))
      .catch(() => setHojaCuerpo(null))
      .finally(() => setCargandoHoja(false));
  }, [hojaAbierta]);

  const [espacio, setEspacio] = useState(null);
  const [completados, setCompletados] = useState([]);

  // ¿LE TOCA EL TEST DEL RETO? · el aviso del menú (Ann, 28-ago-2026).
  // Lo decide el SERVIDOR (`toca_test`), no la pantalla: el día sale de
  // fecha_inicio + cada_dias y si cada uno lo calculara por su cuenta, un día
  // el menú diría una cosa y la pestaña otra.
  /* LA FUENTE DE LA MARCA · Poppins, para TODA la ficha.
     La cargaba solo «Mis pautas», con este mismo trozo dentro de su
     componente: al abrir Entender o Mis cuestionarios, ese componente no está
     montado, Poppins no se descargaba y el titular salía con otra fuente. Se
     veía el mismo tamaño y la misma posición, pero la letra no era la misma
     (Ann, 28-ago-2026: «la posición conseguida y el tamaño, pero no la
     fuente»). Aquí arriba se carga una vez y vale para las cinco pestañas. */
  useEffect(() => {
    const id = 'fvpac-poppins';
    if (document.getElementById(id)) return;
    const l = document.createElement('link');
    l.id = id; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(l);
  }, []);

  const [tocaTest, setTocaTest] = useState(false);
  useEffect(() => {
    fetch('/api/paciente/mi-reto', { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setTocaTest(!!(d && d.toca_test)))
      .catch(() => { /* sin reto o sin red → no se avisa de nada */ });
  }, []);

  const [verVideo, setVerVideo] = useState(null); // ejercicio abierto en el visor
  // El vídeo de presentación arranca con el triangulito encima; al darle, se reproduce.
  const videoPresRef = useRef(null);
  const [presEnMarcha, setPresEnMarcha] = useState(false);
  // Cabeceras de clínica: la LISTA entera, en el orden configurado, y cuál se ve ahora.
  // Antes se pedía "el anuncio activo", que devuelve UNO solo (el más reciente), así que
  // ni rotaba ni respetaba el orden aunque hubiera varias (Ann, 22-jul-2026).
  const [anuncios, setAnuncios] = useState([]);
  const [anuncioIdx, setAnuncioIdx] = useState(0);
  // Días distintos en los que ha completado ejercicios (constancia real).
  const [diasConEjercicios, setDiasConEjercicios] = useState(0);
  // Próxima sesión de fisio (Ann): va arriba del todo para que no se le olvide.
  const [proximaSesion, setProximaSesion] = useState(null);
  // Calendario de la pauta (Ann): una pauta es un hábito diario, no un check único.
  // Cuadraditos por día × momento; verde = hecho. El sub-botón lo muestra/oculta.
  const [calendario, setCalendario] = useState(null);
  const [progMes, setProgMes] = useState(null);   // línea de progresión del mes (al lado del calendario)
  const [progAnio, setProgAnio] = useState(null); // línea de progresión del año (Mi evolución)
  const cargarProgresion = async () => {
    try {
      const rm = await fetch('/api/paciente/pautas/progresion?vista=mes', { headers: { Authorization: `Bearer ${token()}` } });
      if (rm.ok) setProgMes((await rm.json())?.puntos || null);
    } catch (e) { console.error(e); }
    try {
      const ra = await fetch('/api/paciente/pautas/progresion?vista=anio', { headers: { Authorization: `Bearer ${token()}` } });
      if (ra.ok) setProgAnio((await ra.json())?.puntos || null);
    } catch (e) { console.error(e); }
  };
  const marcarEjercicioCal = async (pautaId, fecha, momento, ejercicio, hecho) => {
    // Se pinta al instante y se guarda. El calendario guarda las marcas en `celdas`
    // (formato rejilla), NO en dias[].momentos — actualizar la estructura vieja
    // reventaba el render y "echaba" a la paciente al marcar (Ann, 23-jul-2026).
    setCalendario((c) => (c && Array.isArray(c.celdas)) ? ({ ...c, celdas: c.celdas.map((cel) =>
      (cel.momento === momento && cel.idx === ejercicio && cel.fecha === fecha)
        ? { ...cel, hecho } : cel) }) : c);
    try {
      const r = await fetch('/api/paciente/pautas/calendario/marcar', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pauta_id: pautaId, momento, ejercicio, fecha, hecho }),
      });
      if (!r.ok) { await cargarCalendarioFull(); }
    } catch (e) { console.error(e); await cargarCalendarioFull(); }
    // Al marcar, la progresión cambia: se refresca la gráfica.
    cargarProgresion();
  };
  const cargarCalendarioFull = async () => {
    try {
      const r = await fetch('/api/paciente/pautas/calendario', { headers: { Authorization: `Bearer ${token()}` } });
      if (r.ok) { const d = await r.json(); setCalendario(d?.pauta ? d : null); }
    } catch (e) { console.error(e); }
    await cargarProgresion();
  };
  // Petición de cambio de cita: la paciente PIDE, la clínica decide. Nunca cancela ella.
  const [pidiendoCambio, setPidiendoCambio] = useState(false);
  const [motivoCambio, setMotivoCambio] = useState('');
  const [enviandoCambio, setEnviandoCambio] = useState(false);
  const [avisoCambio, setAvisoCambio] = useState('');
  // Aviso al intentar modificar una cita con menos de 24 h (botón en gris).
  const [avisoNoModif, setAvisoNoModif] = useState(false);
  const pedirCambioCita = async () => {
    if (enviandoCambio) return;
    setEnviandoCambio(true);
    try {
      const r = await fetch('/api/paciente/proxima-sesion/pedir-cambio', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo: motivoCambio.trim() }),
      });
      if (r.ok) {
        setPidiendoCambio(false); setMotivoCambio(''); setAvisoCambio('');
        await cargarDatos(); await cargarChat();
      } else {
        // Nada de fallos mudos: si no ha salido, se dice y por qué.
        let m = 'No se ha podido enviar tu petición. Inténtalo de nuevo en un momento.';
        try { const e = await r.json(); if (e?.detail) m = e.detail; } catch (_) { /* sin cuerpo */ }
        setAvisoCambio(m);
      }
    } catch (e) {
      console.error(e);
      setAvisoCambio('No se ha podido enviar tu petición. Revisa tu conexión e inténtalo de nuevo.');
    } finally { setEnviandoCambio(false); }
  };
  // Guarda true/false: se puede marcar y también rectificar (Ann pidió poder deshacer).
  // Avisos "campanita" (Ann): un aviso de color por tipo que la fisio deja (mensaje /
  // pauta / cuestionario). Sale hasta que el paciente ABRE esa sección; si la fisio
  // deja algo NUEVO (cambia la firma), reaparece. Persistido en localStorage.
  const [avisosVistos, setAvisosVistos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('clinica_avisos_vistos') || '{}'); } catch (e) { return {}; }
  });
  const marcarAvisoVisto = (tipo, firma) => setAvisosVistos((prev) => {
    if (!firma || prev[tipo] === firma) return prev;
    const next = { ...prev, [tipo]: firma };
    try { localStorage.setItem('clinica_avisos_vistos', JSON.stringify(next)); } catch (e) { /* noop */ }
    return next;
  });

  // Cuestionario que el paciente está respondiendo (abre el wizard oficial del paciente).
  const [respondiendo, setRespondiendo] = useState(null);

  // Chat con el fisio (mensajería tipo WhatsApp). El backend saca su paciente_id del
  // token, así que aquí no se manda ningún id: solo puede ver/escribir en SU hilo.
  const [mensajes, setMensajes] = useState([]);
  const [textoChat, setTextoChat] = useState('');
  // Abierto/cerrado del chat de «Mi fisio y yo». Los mensajes son los REALES
  // (`mensajes` / `cargarChat`); aquí no hay hilo aparte.
  const [chatAbiertoFv, setChatAbiertoFv] = useState(false);
  const [enviandoChat, setEnviandoChat] = useState(false);
  // El chat es POR EPISODIO (encargo de Ann): el paciente solo ve el hilo de SU
  // episodio activo, nunca el de otros episodios suyos. Aquí se guarda por qué no
  // hay chat, si es que no lo hay, para poder explicarlo en pantalla.
  const [chatMotivo, setChatMotivo] = useState(null);
  const [errorChat, setErrorChat] = useState('');
  // La caja del chat tiene alto FIJO y scroll propio: al llegar mensajes nuevos baja
  // sola al último, como en el móvil (no crece la página hasta el infinito).
  const chatScrollRef = useRef(null);
  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [mensajes]);
  const cargarChat = async () => {
    try {
      const r = await fetch('/api/paciente/chat/mensajes', { headers: { Authorization: `Bearer ${token()}` } });
      if (r.ok) {
        const d = await r.json();
        // El servidor devuelve { mensajes, episodio_id, motivo }. Se acepta también
        // la lista pelada por si una versión anterior del backend sigue en pie.
        setMensajes(Array.isArray(d) ? d : (d?.mensajes || []));
        setChatMotivo(Array.isArray(d) ? null : (d?.motivo || null));
      }
    } catch (e) { console.error(e); }
  };
  const enviarMensajeChat = async () => {
    const t = textoChat.trim();
    if (!t || enviandoChat) return;
    setEnviandoChat(true);
    try {
      const r = await fetch('/api/paciente/chat/mensaje', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto: t }),
      });
      if (r.ok) { setTextoChat(''); setErrorChat(''); await cargarChat(); }
      else {
        // Nada de fallos mudos: si el mensaje no ha salido, se dice y por qué.
        let motivo = 'No se ha podido enviar tu mensaje. Inténtalo de nuevo en un momento.';
        try { const e = await r.json(); if (e?.detail) motivo = e.detail; } catch (_) { /* sin cuerpo */ }
        setErrorChat(motivo);
      }
    } catch (e) {
      console.error(e);
      setErrorChat('No se ha podido enviar tu mensaje. Revisa tu conexión e inténtalo de nuevo.');
    }
    finally { setEnviandoChat(false); }
  };

  // Carga (o recarga) los datos del espacio. Se llama al montar y al terminar de
  // responder un cuestionario, para que pase de "pendiente" a "completado" al instante.
  const cargarDatos = async () => {
    try {
      const res = await fetch('/api/paciente/mi-espacio', { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) setEspacio(await res.json());
    } catch (e) { console.error(e); }
    try {
      const rc = await fetch('/api/paciente/cuestionarios/completados', { headers: { Authorization: `Bearer ${token()}` } });
      if (rc.ok) setCompletados((await rc.json())?.cuestionarios || []);
    } catch (e) { console.error(e); }
    try {
      const rps = await fetch('/api/paciente/proxima-sesion', { headers: { Authorization: `Bearer ${token()}` } });
      if (rps.ok) setProximaSesion((await rps.json())?.sesion || null);
    } catch (e) { console.error(e); }
    await cargarCalendarioFull();
    try {
      const rcst = await fetch('/api/paciente/pautas/constancia', { headers: { Authorization: `Bearer ${token()}` } });
      if (rcst.ok) setDiasConEjercicios((await rcst.json())?.dias_con_ejercicios || 0);
    } catch (e) { console.error(e); }
    // Ejercicios que ya dio por completados HOY: así al recargar no vuelve a cero.
    try {
      const rh = await fetch('/api/paciente/pautas/hechos-hoy', { headers: { Authorization: `Bearer ${token()}` } });
      if (rh.ok) {
        const d = await rh.json();
        const mapa = {};
        (d?.hechos || []).forEach((h) => { mapa[`${h.pauta_id}-${h.grupo}-${h.ejercicio}`] = true; });
        setHechos(mapa);
      }
    } catch (e) { console.error(e); }
    // Cabeceras de clínica. Se pide el ROTATIVO, que devuelve TODAS las activas en el
    // orden configurado (orden_portada) y con sus segundos. Si no hay ninguna marcada
    // para rotar, se cae al "anuncio activo" de siempre: así una cabecera suelta, sin
    // configurar rotación, se sigue viendo igual que antes.
    try {
      const rr = await fetch('/api/canal-anuncios/wp/rotativo?ambito=clinica&limite=10',
        { headers: { Authorization: `Bearer ${token()}` } });
      const lista = rr.ok ? await rr.json() : [];
      if (Array.isArray(lista) && lista.length) {
        setAnuncios(lista);
      } else {
        const ra = await fetch('/api/canal-anuncios/wp/activo?ambito=clinica',
          { headers: { Authorization: `Bearer ${token()}` } });
        if (ra.ok) { const a = await ra.json(); if (a?.activo) setAnuncios([a]); }
      }
      setAnuncioIdx(0);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    cargarDatos();
    cargarChat();
    // eslint-disable-next-line
  }, []);

  const fisio = espacio?.fisio || null;
  const episodio = espacio?.episodio || null;
  const mensajeFisio = espacio?.mensaje_fisio || '';
  const pautas = espacio?.pautas_domiciliarias || [];
  // Vídeo VERTICAL de presentación del fisio (Tarea A): PRIORIDAD la presentación
  // del PERFIL del fisio (config_profesionales.presentacion_video.url · pública, no
  // caduca); si no la trae, respaldo en el video_bienvenida de la pauta (compat).
  // OJO: `video_bienvenida` NO es una dirección, es una FICHA que guarda el CRM:
  // { video_id, url, titulo, thumbnail } (ver PautaDomiciliaria.jsx al guardar la pauta).
  // Antes se pasaba la ficha ENTERA al reproductor y `getVideoUrl` reventaba con
  // "url.startsWith is not a function", así que el bloque de presentación se rompía
  // justo en el caso que se quiere usar: fisio SIN vídeo en su perfil y vídeo puesto
  // en la pauta. Hay que coger la dirección de dentro (`.url`). Comprobado 22-jul-2026.
  const bienvenidaPauta = pautas.find(p => p?.video_bienvenida?.url)?.video_bienvenida || null;
  const videoPresentacion = (fisio?.presentacion_video?.url) || bienvenidaPauta?.url || null;
  const posterPresentacion = (fisio?.presentacion_video?.thumbnail) || bienvenidaPauta?.thumbnail || undefined;
  const presentacionTexto = fisio?.presentacion_texto || '';
  const pendientes = espacio?.cuestionarios_pendientes || [];
  const nPend = espacio?.total_cuestionarios_pendientes ?? pendientes.length;

  // Para cada cuestionario completado, se anota cuál fue el ANTERIOR del mismo tipo
  // (por fecha), en q.__anterior. Sirve para la etiqueta de evolución.
  (() => {
    const porTipo = {};
    [...completados]
      .sort((a, b) => new Date(a.fecha_completado || a.fecha) - new Date(b.fecha_completado || b.fecha))
      .forEach((q) => {
        const prev = porTipo[q.tipo];
        porTipo[q.tipo] = q;
        q.__anterior = typeof prev?.valor === 'number' ? prev.valor : null;
      });
  })();

  // Mediciones de dolor EVA/VAS del paciente, sacadas de SUS cuestionarios ya
  // completados. Nada calculado ni inventado: el valor tal y como se guardó.
  //
  // SOLO las del EPISODIO ACTIVO (Ann): mezclar episodios en una misma línea es
  // engañoso — una cervicalgia curada hace un año seguida de una lumbalgia nueva
  // dibujaría "80 → 10 → 60" como si la paciente hubiera empeorado, y no es verdad.
  const vasTodos = completados.filter((q) => q.tipo === 'vas_oficial');
  /* ── LO QUE VE ELLA · SOLO EL EPISODIO EN CURSO ──────────────────────────
     Ann, 28-ago-2026: «el paciente ha de ver solamente su evolución global
     desde el inicio del episodio hasta el día presente, tanto en EVA como en
     SF-12 como en el resto. La idea es que tenga los resultados nada más. La
     evolución de todo lo anterior y el detalle es para la administración del
     fisio».

     La ventana es la que ella dice: DESDE la fecha de inicio del episodio
     HASTA hoy. Se filtra por FECHA y no solo por el identificador del
     episodio, porque hay mediciones antiguas guardadas sin episodio asignado:
     por id se colarían igual, y le enseñaríamos dolor de un tratamiento que
     ya terminó. Comparar con eso no la ayuda, la confunde. */
  const dentroDelEpisodio = (fechaISO) => {
    if (!fechaISO) return false;
    const f = String(fechaISO).slice(0, 10);
    const ini = episodio?.fecha_inicio ? String(episodio.fecha_inicio).slice(0, 10) : null;
    const hoy = new Date();
    const hoyISO = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    if (f > hoyISO) return false;              // nada del futuro
    if (!ini) return true;                     // sin episodio abierto, no se recorta
    return f >= ini;
  };

  const vasDelEpisodio = vasTodos.filter((q) => dentroDelEpisodio(q.fecha_completado || q.fecha));

  // ── SF-12 · «Tu salud física y ánimo» ────────────────────────────────
  // Sale del MISMO sitio que el resto: /paciente/cuestionarios/completados,
  // que ya devuelve `resultados`. NO hace falta endpoint nuevo.
  //
  // El CRM guarda las dos puntuaciones oficiales de la escala:
  //     pcs → salud FÍSICA      mcs → ÁNIMO
  // Es exactamente lo que pinta la tarjeta, así que solo se renombra.
  //
  // Si un SF-12 llega sin las dos, se DESCARTA: media tarjeta es peor que
  // ninguna, y aquí no se rellena un hueco con un número inventado.
  const sf12 = useMemo(() => (completados || [])
    .filter((q) => String(q.tipo || '').toLowerCase() === 'sf12')
    .map((q) => {
      // ⚠️ LAS DOS FORMAS conviven en el propio CRM, y hay que aguantar las dos:
      //     {sf12:{pcs,mcs}}   ← anidado por tipo (clinica.py L335)
      //     {pcs,mcs}          ← plano            (clinica.py L1383)
      // Leer solo una deja la tarjeta en blanco con datos que SÍ existen, y
      // sin dar ningún error: el fallo de hoy repetido.
      const r0 = q.resultados || {};
      const r = (r0.sf12 && typeof r0.sf12 === 'object') ? r0.sf12 : r0;
      const f = Number(r.pcs); const a = Number(r.mcs);
      if (!Number.isFinite(f) || !Number.isFinite(a)) return null;
      return { fecha: String(q.fecha_completado || q.fecha || '').slice(0, 10), fisica: f, animo: a };
    })
    .filter((x) => x && x.fecha && dentroDelEpisodio(x.fecha))   // la misma ventana que el EVA
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha)), [completados, episodio]);

  /* ── LOS DEMÁS CUESTIONARIOS ─────────────────────────────────────────────
     Ann: «que todos los cuestionarios que están activados y bien puntuados
     aparezcan en la ficha del paciente si los responde».
     El SF-12 y el ODI tienen tarjeta propia; el resto comparten una, que
     conoce la escala de cada uno. Los que no conoce NO se pintan: antes que
     enseñar un número que puede leerse al revés, no se enseña nada. */
  const otros = useMemo(() => {
    /* ⚠️ El EVA (`vas_oficial`) NO va aquí: ya tiene su propia gráfica de
       evolución más abajo («Mi dolor, de dónde vengo»). Si se añadiera,
       saldría DOS VECES en la misma pantalla — la tarjeta y la línea, con el
       mismo dato. Lo mismo pasa con el SF-12 y el ODI, que tienen tarjeta
       propia clonada del prototipo. Esta lista es solo para los que NO tienen
       forma propia de enseñarse. */
    const TIPOS = ['dolor_mefir', 'efp0', 'psfs'];
    const por = {};
    (completados || []).forEach((q) => {
      const t = String(q.tipo || '').toLowerCase();
      if (!TIPOS.includes(t)) return;
      const fecha = String(q.fecha_completado || q.fecha || '').slice(0, 10);
      if (!fecha || !dentroDelEpisodio(fecha)) return;      // la misma ventana
      if (typeof q.valor !== 'number') return;               // sin número, no se pinta
      (por[t] = por[t] || []).push({ fecha, valor: q.valor });
    });
    Object.values(por).forEach((L) => L.sort((a, b) => new Date(a.fecha) - new Date(b.fecha)));
    return por;
  }, [completados, episodio]);

  // ── ODI · «Espalda (Oswestry)» ───────────────────────────────────────────
  // Las DIEZ secciones se guardan como respuestas odi_1 … odi_10, con valor
  // 0-5, que es exactamente lo que espera la tarjeta. Solo hay que ordenarlas.
  //
  // Si faltan secciones NO se descarta aquí: se manda tal cual con sus huecos
  // y es la tarjeta la que decide — con menos de 8 contestadas no enseña
  // porcentaje, porque un índice a medias es un número falso con pinta de
  // verdadero. Esa regla vive en un solo sitio, no en dos.
  const odi = useMemo(() => (completados || [])
    .filter((q) => String(q.tipo || '').toLowerCase() === 'odi')
    .map((q) => {
      const r = q.respuestas || q.resultados || {};
      const rr = (r.odi && typeof r.odi === 'object') ? r.odi : r;
      const s10 = Array.from({ length: 10 }, (_, i) => {
        const v = Number(rr[`odi_${i + 1}`]);
        return Number.isFinite(v) ? v : null;
      });
      return { fecha: String(q.fecha_completado || q.fecha || '').slice(0, 10), s: s10 };
    })
    .filter((x) => x.fecha && dentroDelEpisodio(x.fecha))   // la misma ventana
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha)), [completados, episodio]);
  const medicionesVas = vasDelEpisodio
    .map((q) => ({ fecha: q.fecha_completado || q.fecha, value: q.valor }))
    .filter((m) => m.fecha && typeof m.value === 'number');
  // Cuestionarios de dolor que quedan FUERA (de otro episodio, o sin episodio
  // asignado). Se dice, no se esconde: así nadie piensa que se han perdido.
  const vasDeOtrosEpisodios = vasTodos.length - vasDelEpisodio.length;

  // Saludo: fecha larga en español y nombre de pila del paciente.
  const primerNombrePaciente = (user?.nombre || '').trim().split(' ')[0] || 'hola';
  const fechaLarga = (() => {
    try {
      // Solo la PRIMERA letra en mayúscula ("Miércoles, 22 de julio"), no cada palabra.
      const t = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
      return t.charAt(0).toUpperCase() + t.slice(1);
    } catch (e) { return ''; }
  })();

  // ── MINI RESUMEN de la recuperación (solo datos REALES, nada inventado) ──
  // · cuestionarios completados → los que devuelve el backend
  // · días de recuperación → desde fecha_inicio del episodio abierto
  // · ejercicios de la pauta de hoy → los de la pauta en vivo
  // Lo que NO se guarda todavía (los ejercicios marcados como hechos) no se muestra:
  // preferimos no enseñar un número antes que enseñarlo mal.
  const diasRecuperacion = (() => {
    const ini = episodio?.fecha_inicio;
    if (!ini) return null;
    const d = new Date(String(ini).slice(0, 10));
    if (isNaN(d.getTime())) return null;
    const dias = Math.floor((Date.now() - d.getTime()) / 86400000);
    return dias >= 0 ? dias : null;
  })();
  // Fecha de inicio del episodio en texto ("3 de julio") · para «Mi resumen».
  const fechaIniEpisodio = (() => {
    const ini = episodio?.fecha_inicio;
    if (!ini) return '';
    try {
      const d = new Date(String(ini).slice(0, 10));
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
    } catch (e) { return ''; }
  })();
  const totalEjerciciosPauta = pautas.reduce(
    (n, p) => n + (p.grupos || []).reduce((m, g) => m + (g.ejercicios || []).length, 0), 0);
  const mensajeAnimo = (() => {
    if (completados.length === 0 && !diasRecuperacion) return 'Bienvenida a tu espacio. Aquí verás tus pautas, tus cuestionarios y podrás escribir a tu fisio.';
    if (totalEjerciciosPauta > 0) return 'Cada día que haces tus ejercicios sumas. Ve a tu ritmo y cuéntale a tu fisio cómo te encuentras.';
    return 'Vas avanzando. Cuando tu fisio te deje algo nuevo, te avisamos aquí arriba.';
  })();

  // Firma de cada tipo (cambia cuando la fisio deja algo nuevo). Vacía = no hay nada.
  const firmaMensaje = mensajeFisio ? `m:${mensajeFisio.length}:${mensajeFisio.slice(0, 24)}` : '';
  const firmaPauta = pautas.length ? `p:${pautas.map((p) => p.id).join(',')}` : '';
  const firmaCuest = pendientes.length ? `c:${pendientes.map((q) => q.id).join(',')}` : '';
  // LA CAMPANITA DEL CHAT · antes estaba clavada a 0 y no se encendía nunca, así que
  // un mensaje de la fisio no avisaba de nada. Se calcula con el MISMO sistema de
  // firma que los otros avisos: la firma es el último mensaje SUYO; si cambia desde
  // la última vez que el paciente abrió el chat, hay algo por leer.
  const ultimoDeLaFisio = (() => {
    for (let i = mensajes.length - 1; i >= 0; i--) {
      if (mensajes[i].lado !== 'paciente') return mensajes[i];
    }
    return null;
  })();
  const firmaChat = ultimoDeLaFisio ? `ch:${ultimoDeLaFisio.id}` : '';
  // Al CAMBIAR de pestaña, ese aviso queda "visto" y se apaga. Se salta la primera
  // carga para que al entrar se vean las 3 campanitas (no se apaguen solas en 'mifisio').
  const primeraVezRef = useRef(true);
  useEffect(() => {
    if (primeraVezRef.current) { primeraVezRef.current = false; return; }
    // El escrito de la fisio vive ahora en "Mis pautas": la campanita violeta se
    // apaga al abrir esa pestaña, que es donde el paciente lo lee.
    if (tab === 'pautas') { marcarAvisoVisto('mensaje', firmaMensaje); marcarAvisoVisto('pauta', firmaPauta); }
    if (tab === 'cuestionarios') marcarAvisoVisto('cuestionario', firmaCuest);
    // eslint-disable-next-line
  }, [tab]);
  // Los avisos se calculan con una FOTO de lo ya visto tomada AL ENTRAR, no con el
  // estado vivo. Si no, al abrir una sección su campanita desaparecía al instante y
  // toda la pantalla saltaba hacia arriba mientras el paciente navegaba (Ann: "se mueve
  // el escenario"). Visto sí se guarda: la campanita ya no sale la PRÓXIMA vez que entra.
  const vistosAlEntrar = useRef(avisosVistos).current;
  // Cada campanita: color por sección (violeta mensaje · verde pauta · naranja cuestionario).
  const avisos = [
    firmaMensaje && vistosAlEntrar.mensaje !== firmaMensaje && {
      color: '#7c3aed', bg: '#f4f0fe', borde: '#e4d9fb',
      texto: 'Tu fisioterapeuta tiene un mensaje para ti.', cta: 'Ir al mensaje', ir: 'pautas',
    },
    firmaPauta && vistosAlEntrar.pauta !== firmaPauta && {
      color: '#059669', bg: '#e8f7f0', borde: '#c9ecdd',
      texto: 'Tu fisioterapeuta te ha dejado una nueva pauta domiciliaria.', cta: 'Ir a la pauta', ir: 'pautas',
    },
    firmaCuest && vistosAlEntrar.cuestionario !== firmaCuest && {
      color: '#ea580c', bg: '#fdf1e7', borde: '#f8ddc4',
      texto: 'Tu fisioterapeuta te ha dejado un nuevo cuestionario.', cta: 'Ir al cuestionario', ir: 'cuestionarios',
    },
  ].filter(Boolean);

  // Cabecera tipo anuncio (Ann): imagen con texto encima, junto al logo MEFIR.
  // La manda SIEMPRE el backend: el anuncio de ámbito=clínica ("cabeceras de clínica").
  // Si no hay anuncio activo, NO se pinta cabecera: hueco limpio.
  //
  // Aquí había una cabecera de mentira para maquetar ("Convenio de colaboración con el
  // BSI de Quirón") que salía en local mientras cargaba el anuncio de verdad. Fuera:
  // en el CRM no se enseña nunca un dato inventado, ni un segundo, ni en local — se
  // confunde con información real de la clínica (Ann, 22-jul-2026).
  const cabecera = anuncios[anuncioIdx] || null;

  // ROTACIÓN de la cabecera: pasa a la siguiente cada X segundos, en el orden que se
  // configuró. Los segundos los decide cada cabecera en el editor; si no se han puesto,
  // 6 s. Con una sola cabecera no hay temporizador (nada que rotar).
  useEffect(() => {
    if (anuncios.length < 2) return undefined;
    const seg = Number(anuncios[anuncioIdx]?.segundos) > 0 ? Number(anuncios[anuncioIdx].segundos) : 6;
    const t = setTimeout(() => setAnuncioIdx((i) => (i + 1) % anuncios.length), seg * 1000);
    return () => clearTimeout(t);
  }, [anuncios, anuncioIdx]);

  return (
    <div className="crm-portal">

      {/* RESPONDER CUESTIONARIO · usa el wizard OFICIAL del paciente
          (components/patient/PatientQuestionnaireWizard): tiene los 9 cuestionarios con
          su esquema, consentimiento, firma, puntuación y el envío al backend.
          Al terminar recargamos para que pase de "pendiente" a "completado". */}
      {respondiendo && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: '#fff', overflowY: 'auto' }}>
          <PatientQuestionnaireWizard
            cuestionarioId={respondiendo.id}
            cuestionarioTipo={respondiendo.tipo}
            cuestionarioNombre={respondiendo.nombre || respondiendo.tipo}
            onComplete={() => { setRespondiendo(null); cargarDatos(); }}
            onCancel={() => setRespondiendo(null)}
          />
        </div>
      )}
      {/* ⚠️ TEMPORAL (solo local · QUITAR AL FINAL). Botón para refrescar y ver los
          cambios sin salir de la pantalla. Gateado a localhost → NUNCA aparece en prod. */}
      {typeof window !== 'undefined' && ['localhost','127.0.0.1'].includes(window.location.hostname) && (
        <button
          onClick={() => window.location.reload()}
          title="Refrescar (solo local)"
          style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 90, width: 48, height: 48,
            borderRadius: '50%', border: 'none', cursor: 'pointer', color: '#fff',
            background: 'linear-gradient(135deg,#3C8696,#2C6675)', boxShadow: '0 6px 18px rgba(44,107,118,.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <RefreshCw size={22} />
        </button>
      )}
      {/* Cabecera ÚNICA de sección arriba (misma barra teal que "Clases Online") */}
      {/* Sin el rótulo "Mi Clínica" (Ann): manda el LOGO de MEFIR, a la derecha. El
          nombre de la paciente ya está abajo en el saludo, así que aquí sobraba. */}
      <ClientSectionHeader
        logoSrc="/logo-mefir-blanco.png"
        logoAlt="Instituto MEFIR"
        onBack={onBackToSelector}
        onLogout={onLogout}
        wrapClass="md:max-w-[1120px] md:mx-auto relative z-10"
      />
      {/* Bloque blanco: logo MEFIR a la izquierda + cabecera-anuncio (imagen con texto)
          a su lado. La cabecera viene del backend (anuncio ámbito=clínica). */}
      {/* El logo se ha subido a la barra teal (Ann), así que aquí la carátula se queda
          SOLA y va de lado a lado, como en Canal Vídeos. */}
      <div className="crm-portal-logo crm-portal-logo--solo-caratula">
        {cabecera && (
          <div className="crm-portal-cabecera"
               /* OJO con `background` y `backgroundImage` JUNTOS: `background` es una
                  propiedad-resumen y al aplicarla BORRA la imagen puesta antes. Aquí
                  iba después y valía undefined, así que React la escribía vacía y se
                  llevaba por delante la foto: se leían el título y el subtítulo, pero
                  la imagen de la cabecera no salía nunca (22-jul-2026, Guardián).
                  Se pone UNA de las dos, nunca las dos. NO volver a juntarlas.
                  Medidas (Ann): alta, con tope de ancho y pegada a la derecha. */
               /* De lado a lado (Ann): sin tope de ancho ni margen. */
               style={{ position: 'relative', flex: '1 1 100%', width: '100%',
                 height: 165, borderRadius: 0,
                 overflow: 'hidden',
                 ...(cabecera.imagen_fondo_url
                   ? { backgroundImage: `url("${cabecera.imagen_fondo_url}")`,
                       backgroundSize: 'cover', backgroundPosition: 'center' }
                   : { background: cabecera.color_fondo || '#2C6675' }) }}>
            {/* El VELO lo define quien diseña la cabecera en el backend (overlay_tipo,
                overlay_color, overlay_alpha), igual que el banner del canal. Antes había
                un velo teal fijo y muy opaco que TAPABA la imagen y el diseño de Ann. */}
            {cabecera.imagen_fondo_url && (() => {
              const tipo = cabecera.overlay_tipo || 'solido';
              if (tipo === 'ninguno') return null;
              const h = (cabecera.overlay_color || '#000000').replace('#', '');
              const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), bl = parseInt(h.slice(4, 6), 16);
              const a = cabecera.overlay_alpha ?? 0.3;
              const rgba = `rgba(${r},${g},${bl},${a})`;
              return <div style={{ position: 'absolute', inset: 0,
                background: tipo === 'degradado' ? `linear-gradient(to bottom, transparent 0%, ${rgba} 100%)` : rgba }} />;
            })()}
            {/* Sin disolución en degradado: Ann la quiere SÓLIDA (22-jul-2026). */}
            <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column',
              justifyContent: 'center', padding: '0 20px', color: cabecera.color_texto || '#fff' }}>
              {cabecera.titulo && (
                <div style={{ fontSize: TAMANO[cabecera.tamano_titulo] || TAMANO.md, fontWeight: 700, lineHeight: 1.2,
                  fontFamily: cabecera.fuente_titulo || undefined }}>{cabecera.titulo}</div>
              )}
              {cabecera.subtitulo && (
                <div style={{ fontSize: TAMANO[cabecera.tamano_subtitulo] || TAMANO.sm, marginTop: 2,
                  color: cabecera.color_subtitulo || undefined, opacity: cabecera.color_subtitulo ? 1 : 0.92,
                  fontFamily: cabecera.fuente_subtitulo || undefined,
                  fontWeight: cabecera.negrita_subtitulo ? 700 : undefined }}>{cabecera.subtitulo}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── SALUDO (Ann 19-ago): fecha + Hola + episodio activo, entre banner y menú ── */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '14px 28px 2px',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: '#4a5568', fontSize: 13.5 }}>{fechaLarga}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#141a22', lineHeight: 1.15 }}>
            Hola, {primerNombrePaciente}
          </div>
        </div>
        {episodio?.nombre && (
          <div className="crm-portal-episodio">
            <span className="crm-portal-episodio__punto" />
            Episodio activo <b>{episodio.nombre}</b>
          </div>
        )}
      </div>

      {/* ── MENÚ FisioVisión (Ann 19-ago): SOLO el menú, sin contenido todavía ── */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '14px 28px 0' }}>
        <nav className="fv-nav">
          {[
            ['mifisio', 'Mi fisio y yo'],
            ['pautas', 'Mis pautas'],
            ['entender', 'Entender'],
            ['cuestionarios', 'Mis cuestionarios'],
            ['motivacion', 'Mi motivación'],
          ].map(([id, label]) => (
            <button key={id} type="button"
              className={'fv-nav__btn' + (tab === id ? ' is-on' : '')}
              onClick={() => setTab(id)}>
              {label}
              {id === 'mifisio' && <span className="fv-campanita fv-campanita--nav"><Bell size={14} /></span>}
              {/* EL AVISO DE CUESTIONARIO PENDIENTE · clonado de FisioVisión
                  (pintarAvisoCuest, Ann 13-ago-2026). El CSS estaba entero en
                  el CRM —badge y el ocre de «--nuevo»— y no lo pintaba nadie:
                  si su fisio le lanzaba un cuestionario, en el menú no se
                  enteraba.
                  VA EN OCRE, NO EN VERDE, y es decisión de Ann: el verde ya
                  quiere decir «tu fisio te ha escrito» y no puede querer decir
                  dos cosas. Ocre = algo te espera. */}
              {/* EL DÍA DEL TEST DEL RETO · mismo ocre que el de cuestionarios:
                  ocre = algo te espera. Sin número, porque no es una cuenta:
                  es una cosa, y un «1» ahí no diría nada. */}
              {id === 'motivacion' && tocaTest && (
                <span className="fv-nav__badge fv-nav__badge--nuevo" title="Hoy toca hacer tu test">·</span>
              )}
              {id === 'cuestionarios' && pendientes.length > 0 && (
                <span className="fv-nav__badge fv-nav__badge--nuevo"
                  title={pendientes.length === 1 ? 'Tienes un cuestionario nuevo' : `Tienes ${pendientes.length} cuestionarios nuevos`}>
                  {pendientes.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Contenido de «Mi fisio y yo» (diseño nuevo FisioVisión, rescatado) ── */}
      {tab === 'mifisio' && (() => {
        const fisioReal = fisio;
        const nombreCorto = ((espacio && espacio.paciente && espacio.paciente.nombre) || primerNombrePaciente || '').trim();
        const inicialPac = (nombreCorto[0] || '·').toUpperCase();
        const episodioNom = (episodio && episodio.nombre) || '';
        const msgFisio = (mensajeFisio || presentacionTexto || '').trim();
        const motivacionReal = '';
        // Chat (manual): CHAT_ACTIVO es permiso del fisio por paciente (pendiente de backend →
        // por ahora activo); cerrado si no hay tratamiento (chatMotivo) o si el fisio lo cierra.
        const chatActivo = true;
        const chatCerrado = (chatMotivo === 'sin_episodio') || !chatActivo;
        // ¿tiene algo por leer? Firma del último mensaje de la fisio contra lo ya visto.
        // Se usa la FOTO tomada al entrar (vistosAlEntrar), igual que los demás avisos:
        // si se mirara el estado vivo, la campanita se apagaría sola delante del
        // paciente y la pantalla daría un salto (Ann: «se mueve el escenario»).
        const sinLeer = (firmaChat && vistosAlEntrar.chat !== firmaChat) ? 1 : 0;
        // «hoy» · «ayer» · el día. Sin esto el aviso no dice si es de ahora o de la
        // semana pasada, y no es lo mismo para quien está esperando respuesta.
        const cuandoEscribio = (() => {
          if (!ultimoDeLaFisio || !ultimoDeLaFisio.created_at) return '';
          const d = new Date(ultimoDeLaFisio.created_at);
          if (Number.isNaN(d.getTime())) return '';
          const hoy = new Date();
          const dias = Math.round((new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
            - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
          if (dias <= 0) return ' hoy';
          if (dias === 1) return ' ayer';
          return ' el ' + d.toLocaleDateString('es-ES', { weekday: 'long' });
        })();
        const fisioNombreCorto = (fisioReal && fisioReal.nombre) ? fisioReal.nombre.split(' ')[0] : 'Tu fisio';
        return (
          <div style={{ maxWidth: 1120, margin: '0 auto', padding: '4px 28px 24px' }}>
            <div className="fv-bloques">
              <div className="fv-bloque-instrucciones">
                <div className="ficha-fisio">
                  <span className="crm-portal-enunciado crm-portal-enunciado--fisio">Mi fisioterapeuta</span>
                  <div className="cabeza">
                    <div className="foto">
                      {fisioReal && fisioReal.foto_perfil
                        ? <img src={fisioReal.foto_perfil} alt={fisioReal.nombre || 'Fisioterapeuta'} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                        : (fisioReal ? (fisioReal.nombre || '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') : '·')}
                    </div>
                    <div>
                      <div className="nombre">{fisioReal ? fisioReal.nombre : 'Sin fisioterapeuta asignado'}</div>
                      <div className="cargo">{fisioReal ? ('Fisioterapeuta' + (fisioReal.numero_colegiado ? ' · col. ' + fisioReal.numero_colegiado : '')) : 'Aún no tienes fisio asignado'}</div>
                    </div>
                  </div>
                  <div className="mensaje fv-voz-fisio">
                    <span className="crm-portal-enunciado crm-portal-enunciado--fisio">Mensaje de {fisioReal && fisioReal.nombre ? fisioReal.nombre.split(' ')[0] : 'tu fisio'}</span>
                    {msgFisio
                      ? <p>«{msgFisio}»</p>
                      : <p style={{ color: '#93A0B5' }}>Aún no tienes ningún mensaje esta semana.</p>}
                  </div>
                </div>

                {/* «Entender mi (patología)» · UNA tarjeta por cada hoja que el fisio le
                    haya asignado (dato REAL: /paciente/mis-hojas-entender). Al pulsar se
                    abre la pestaña «Entender», donde se leen con calma. Sin hojas → nada. */}
                {hojasEntender.map((h) => (
                  <button key={h.hoja_id} type="button" className="boton_hoja" onClick={() => setTab('entender')}>
                    {/* el libro ABIERTO, de dos páginas · es la puerta a leer, no un documento */}
                    <span className="boton_hoja__ico">{fvSvg('<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H12v18H6.5A2.5 2.5 0 0 1 4 18.5z"/><path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H12v18h5.5a2.5 2.5 0 0 0 2.5-2.5z"/>', 26)}</span>
                    <span className="boton_hoja__txt">
                      {/* «Entender TU escoliosis» → «Entender MI escoliosis». En el catálogo
                          la hoja es de todos; aquí es SUYA, y el pronombre lo dice. */}
                      <b>{(h.titulo || `Entender mi ${(h.zona || '').toLowerCase()}`).replace(/^Entender tu /, 'Entender mi ')}</b>
                    </span>
                    {/* LA FLECHA · sin ella no se lee como «esto lleva a algún sitio» */}
                    <svg className="boton_hoja__fl" width="18" height="18" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                ))}

                {/* CHAT · plegado por defecto (manual 13-ago). Conectado al chat REAL /paciente/chat.
                    Cerrado por el fisio/sin tratamiento → gris + candado, NO se esconde. */}
                {chatCerrado ? (
                  <button type="button" className="fv-chat-boton es-cerrado" disabled style={{ opacity: 0.62, cursor: 'default' }}>
                    <span className="fv-chat-boton__ico">{fvSvg('<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>', 26)}</span>
                    <span className="fv-chat-boton__txt">
                      <b>Comunicar con mi fisio</b>
                      <small>{chatMotivo === 'sin_episodio'
                        ? 'Esta conversación se abre con tu tratamiento.'
                        : 'Tu fisioterapeuta prefiere que lo habléis en la consulta.'}</small>
                    </span>
                  </button>
                ) : !chatAbiertoFv ? (
                  <button type="button" className="fv-chat-boton" onClick={() => { setChatAbiertoFv(true); marcarAvisoVisto('chat', firmaChat); }}>
                    <span className="fv-chat-boton__ico">{fvSvg('<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>', 26)}</span>
                    <span className="fv-chat-boton__txt">
                      <b>Comunicar con mi fisio</b>
                      {/* Se promete POCO a propósito: sin esta frase el paciente espera
                          como en WhatsApp y a las dos horas cree que su fisio pasa de él. */}
                      <small>{sinLeer > 0 ? `${fisioNombreCorto} te ha escrito${cuandoEscribio}` : 'Las respuestas pueden demorarse unos días'}</small>
                    </span>
                    {sinLeer > 0 && <span className="fv-campanita">{fvSvg('<path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 01-3.4 0"/>', 18)}</span>}
                  </button>
                ) : (
                  <div className="fv-bloque-comunicacion abierto">
                    <div className="fv-chat__cab">
                      <b>Chat con {fisioNombreCorto}</b>
                      <button type="button" className="fv-chat__cerrar" onClick={() => setChatAbiertoFv(false)} aria-label="Cerrar">✕</button>
                    </div>
                    <div ref={chatScrollRef} className="fv-chat__hilo" style={{ height: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 2px' }}>
                      {/* Espacio tipo WhatsApp: los mensajes se pegan ABAJO; el hueco queda arriba. */}
                      <div style={{ flexGrow: 1, minHeight: 8 }} />
                      {mensajes.length === 0 && (
                        <p style={{ color: '#93A0B5', fontSize: 14, textAlign: 'center', margin: '0 auto' }}>Aún no hay mensajes.<br />Escribe el primero cuando lo necesites.</p>
                      )}
                      {mensajes.map((m, idx) => {
                        const mio = m.lado === 'paciente';
                        // SEPARADOR DE DÍA · en una conversación de semanas, sin esto no se
                        // sabe si «19:20» fue anoche o hace un mes. Solo cuando cambia el día.
                        const diaDe = (iso) => {
                          if (!iso) return '';
                          const d = new Date(iso);
                          return Number.isNaN(d.getTime()) ? '' : d.toDateString();
                        };
                        const diaEste = diaDe(m.created_at);
                        const diaPrev = idx > 0 ? diaDe(mensajes[idx - 1].created_at) : '';
                        const rotuloDia = (() => {
                          if (!diaEste || diaEste === diaPrev) return null;
                          const d = new Date(m.created_at);
                          const hoy = new Date();
                          const dias = Math.round((new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
                            - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
                          if (dias <= 0) return 'Hoy';
                          if (dias === 1) return 'Ayer';
                          return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
                            .replace(/^./, (c) => c.toUpperCase());
                        })();
                        return (
                          <React.Fragment key={m.id}>
                          {rotuloDia && <div className="fv-chat__dia">{rotuloDia}</div>}
                          <div style={{ alignSelf: mio ? 'flex-end' : 'flex-start', maxWidth: 'min(82%, 440px)' }}>
                            <div style={{ background: mio ? '#FAF1E6' : '#F1ECFD', color: '#141a22', padding: '9px 13px',
                              borderRadius: mio ? '14px 14px 4px 14px' : '14px 14px 14px 4px', fontSize: 14.5, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                              {m.texto}
                            </div>
                            <div style={{ fontSize: 11, color: '#333d4b', marginTop: 3, textAlign: mio ? 'right' : 'left' }}>
                              {!mio && `${m.autor_nombre || 'Tu fisio'} · `}{fmtHora(m.created_at)}
                            </div>
                          </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                    {/* Caritas (manual §5): 9 emojis; añaden al cuadro, no envían. */}
                    <div className="fv-chat__caritas" style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', justifyContent: 'space-between', padding: '8px 0' }}>
                      {[['👍', 'Muy bien'], ['🙂', 'Bien'], ['❤️', 'Gracias'], ['💚', 'Ánimo'], ['💪', 'Con fuerza'], ['😉', 'Vale'], ['🙏', 'Gracias'], ['👏', 'Genial'], ['😕', 'No lo tengo claro']].map(([e, t]) => (
                        <button key={t} type="button" title={t} onClick={() => setTextoChat((x) => x + e)}
                          style={{ flex: '0 0 auto', width: 42, height: 42, borderRadius: 12, border: '1px solid #CDE9ED', background: '#fff', fontSize: 20, cursor: 'pointer' }}>{e}</button>
                      ))}
                    </div>
                    <div className="fv-chat__escribir" style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                      <textarea value={textoChat}
                        onChange={(e) => setTextoChat(e.target.value)}
                        onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 130) + 'px'; }}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarMensajeChat(); } }}
                        placeholder="Escribe tu mensaje…" rows={1}
                        style={{ flex: 1, resize: 'none', minHeight: 48, maxHeight: 130, padding: '13px 16px',
                          borderRadius: 16, border: '1.5px solid #cfe0e3', fontSize: 15.5, fontFamily: 'inherit',
                          lineHeight: 1.4, outline: 'none', background: '#fff' }} />
                      <button type="button" onClick={enviarMensajeChat}
                        disabled={!textoChat.trim() || enviandoChat} aria-label="Enviar mensaje"
                        style={{ flexShrink: 0, width: 48, height: 48, borderRadius: '50%', border: 'none',
                          cursor: (!textoChat.trim() || enviandoChat) ? 'default' : 'pointer', color: '#fff',
                          background: (!textoChat.trim() || enviandoChat) ? '#C3D2CE' : '#12ACB9',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .15s' }}>
                        <Send size={20} />
                      </button>
                    </div>
                    {errorChat && <div style={{ color: '#7a5b1f', fontSize: 13, marginTop: 6 }}>{errorChat}</div>}
                    <div className="fv-chat__urgencias">
                      Esto no es un servicio de urgencias. Si te encuentras mal o necesitas ayuda ya, llama a tu clínica o, si es grave, al 112.
                      {' '}<span style={{ color: '#93A0B5' }}>(teléfono de la clínica · pendiente de conectar)</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="fv-bloque-progresion">
                <div className="fv-perfil">
                  <span className="crm-portal-enunciado es-oscuro">Mi perfil</span>
                  <div className="fv-perfil__cab">
                    <div className="fv-perfil__foto">
                      {espacio?.paciente?.foto_perfil
                        ? <img src={espacio.paciente.foto_perfil} alt={nombreCorto || 'Paciente'}
                               style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                        : <span className="fv-perfil__ini">{inicialPac}</span>}
                    </div>
                    <div>
                      <div className="fv-perfil__nombre">{nombreCorto || 'Paciente'}</div>
                      <div className="fv-perfil__epi"><span className="fv-perfil__punto" />{episodioNom || 'Sin episodio activo'}</div>
                    </div>
                  </div>
                </div>

                {motivacionReal && (
                  <div className="crm-portal-bloque-terapeutico">
                    <span className="crm-portal-enunciado crm-portal-enunciado--paciente">Mi motivación</span>
                    <q>{motivacionReal}</q>
                  </div>
                )}

                <span className="crm-portal-enunciado es-oscuro">Mi resumen</span>
                <div className="fv-datos">
                  <div className="fv-dato"><b>{diasRecuperacion != null ? diasRecuperacion : '—'}</b><small>días de recuperación{fechaIniEpisodio && <i>desde el {fechaIniEpisodio}</i>}</small></div>
                  <div className="fv-dato"><b>{diasConEjercicios}</b><small>días con ejercicios</small></div>
                  <div className="fv-dato"><b>{completados.length}</b><small>cuestionarios hechos{nPend > 0 && <i>{nPend} {nPend === 1 ? 'te queda' : 'te quedan'}</i>}</small></div>
                </div>

                {/* «Mi semana» (gráfica) y «Mi configuración» (preferencia horaria 9:00)
                    OCULTAS: eran dato DEMO (FV_SEMANA y la hora hardcodeada), sin fuente
                    real atada. «Dato real o no se pinta». Se re-activan cuando haya dato
                    real (agregado semanal + preferencia horaria guardada). */}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── MIS PAUTAS · el componente React de :3004 (MisPautasPaciente) — Ann: «lo que
          está bien de esa es pautas» — pero con el DATO REAL de la paciente
          (/paciente/mis-pautas-fisiovision, misma pautas_domiciliarias), no el catálogo. ── */}
      {tab === 'pautas' && (
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '4px 28px 24px' }}>
          <MisPautasPaciente
            endpoint="/paciente/mis-pautas-fisiovision"
            /* La nota de cada ejercicio la firma su fisio: hace falta su
               nombre y su cara, o el texto se lee como un aviso del programa
               y no como algo que le dice ELLA a ella. */
            fisio={fisio ? { nombre: fisio.nombre || '', foto: fisio.foto_perfil || '' } : null}
            datosPdf={{
              paciente: [espacio?.paciente?.nombre, espacio?.paciente?.primer_apellido].filter(Boolean).join(' ') || primerNombrePaciente || 'Paciente',
              zona: (episodio && episodio.nombre) || '',
              clinica: 'Instituto MEFIR',
              fisio: (fisio && fisio.nombre) || '',
            }}
          />
        </div>
      )}

      {/* ── MI MOTIVACIÓN · componente React con el reto REAL (/paciente/mi-reto, 4e).
          `soloReto`: se pinta SOLO el reto real (o el texto honesto si no hay). La
          constancia (hoy inventada) y el test (fase 2) quedan OCULTOS hasta tener dato
          real — no se enseña registro fabricado. Constancia real = fase datos con 4e. ── */}
      {tab === 'motivacion' && (
        <div className="fv-pagina">
          <MiMotivacionPaciente endpoint="/paciente/mi-reto" soloReto />
        </div>
      )}

      {/* ── ENTENDER · las hojas educativas que el fisio ha asignado al paciente
          (dato REAL: /paciente/mis-hojas-entender). Sin hojas → mensaje honesto. ── */}
      {tab === 'entender' && (() => {
        // Las zonas se CALCULAN de las hojas que hay. Norma de Ann: nada de
        // pestañas que no lleven a ningún sitio — una «Rodilla» que da lista
        // vacía es peor que no tenerla.
        // 🔴 SOLO LAS QUE LE HA MANDADO SU FISIO. Cambio de diseño de Ann
        // (28-ago-2026): antes se enseñaba el catálogo entero con las suyas
        // resaltadas. Ya no. Una hoja que su fisio NO le ha mandado no es
        // suya, y verla ahí la invita a leer sobre algo que no tiene.
        const mias = (catalogo.hojas || []).filter((h) => h.asignada);
        // Las zonas se CALCULAN de las hojas QUE ELLA TIENE, no del catálogo.
        // Norma de Ann: nada de pestañas que no lleven a ningún sitio — una
        // «Rodilla» que da lista vacía es peor que no tenerla.
        const zonas = [...new Set(mias.map((h) => h.zona).filter(Boolean))].sort();
        // Red de seguridad: si la zona elegida ya no existe, se vuelve a «todas»
        // ANTES de pintar. Si no, se queda mirando una lista vacía sin saber por qué.
        const zonaOk = (zonaVista !== 'todas' && !zonas.includes(zonaVista)) ? 'todas' : zonaVista;
        const visibles = mias.filter((h) => zonaOk === 'todas' || h.zona === zonaOk);
        const abierta = hojaAbierta && mias.find((h) => h.hoja_id === hojaAbierta);

        // ── LA HOJA ABIERTA ──
        // El HTML lo genera el render de FisioVisión (hojaEntenderHTML.js), que
        // es la función verHoja() del prototipo COPIADA. Aquí no se maqueta
        // nada: solo se le pasan los datos del CRM y se pinta lo que devuelve.
        if (abierta) {
          const H = hojaCuerpo
            ? { ...hojaCuerpo, mensaje_fisio: hojaCuerpo.nota_fisio || '' }
            : null;
          // ⚠️ `fvpac` NO es decorativo: TODO el CSS de la hoja está acotado a
          // esa clase. Sin ella la hoja sale con la estructura bien y sin un
          // solo estilo.
          return (
            <div className="fvpac" style={{ maxWidth: 900, margin: '0 auto', padding: '4px 28px 24px', background: 'transparent' }}>
              {cargandoHoja && !H && <p style={{ color: '#6B778F' }}>Abriendo tu hoja…</p>}
              {H && (
                <div
                  onClick={(ev) => {
                    // los botones que el HTML trae marcados
                    const b = ev.target.closest('[data-atras],[data-ir],[data-ej]');
                    if (!b) return;
                    if (b.dataset.atras) setHojaAbierta(null);
                    else setTab('pautas');
                  }}
                  dangerouslySetInnerHTML={{
                    __html: verHojaHTML(H, pautaHoja.ejercicios || [],
                      (q) => (pautaHoja.pasos || {})[String(q)] || null, 1,
                      // su fisio DE VERDAD · el mismo mapeo que en la ficha
                      fisio ? { nombre: fisio.nombre || '', foto: fisio.foto_perfil || '' } : null),
                  }}
                />
              )}
            </div>
          );
        }

        // ── EL LISTADO ──
        return (
          <div className="fvpac fv-pagina" style={{ background: 'transparent' }}>
            <div className="fvpac__titular">
              <h1>Entender mi patología</h1>
              {catalogoCargado && visibles.length > 0 && (
                <span className="fvpac__fecha">{visibles.length} {visibles.length === 1 ? 'hoja' : 'hojas'}</span>
              )}
            </div>

            {/* el menú solo aparece si hay más de una zona: con una sola no filtra nada */}
            {zonas.length > 1 && (
              <div className="fv-zonas">
                <button type="button" className={'fv-zona' + (zonaOk === 'todas' ? ' is-on' : '')}
                  onClick={() => setZonaVista('todas')}>Todas</button>
                {zonas.map((z) => (
                  <button key={z} type="button" className={'fv-zona' + (zonaOk === z ? ' is-on' : '')}
                    onClick={() => setZonaVista(z)}>{z}</button>
                ))}
              </div>
            )}

            {/* cargando NO es lo mismo que «no hay»: no se dice que está vacío
                hasta que se sabe (Ann). */}
            {!catalogoCargado ? null : visibles.length === 0 ? (
              <div className="fv-entender-vacio">
                Aquí verás lo que tu fisio te explique de tu caso, para leerlo con calma
                en casa. Todavía no hay nada: cuando lo haya, aparecerá aquí.
              </div>
            ) : visibles.map((h) => (
              <button key={h.hoja_id} type="button"
                className={'fv-entender' + (h.asignada ? ' es-mia' : '')}
                onClick={() => setHojaAbierta(h.hoja_id)}>
                <span className="fv-entender__mini">
                  {h.hero
                    ? <img src={h.hero} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : fvSvg('<rect x="3" y="4" width="18" height="16" rx="3"/><circle cx="9" cy="10" r="2"/><path d="M3 17l5-4 4 3 3-2 6 5"/>', 26)}
                </span>
                <span className="fv-entender__tx">
                  <em>{h.zona || 'General'}</em>
                  <b>{h.titulo}</b>
                  {h.subtitulo && <small>{h.subtitulo}</small>}
                </span>
                {/* La etiqueta «Mi fisio te la ha enviado» se retira: ahora TODAS
                    son suyas, así que decirlo en cada una no informa de nada —
                    solo mete ruido. Tenía sentido cuando convivían con el
                    catálogo. (Ann, 28-ago-2026: solo asignadas.) */}
                <svg className="fv-entender__fl" width="17" height="17" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            ))}
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════════
          MIS CUESTIONARIOS
          EL ORDEN CUENTA UNA HISTORIA (Ann, 13-ago-2026):
            1 · lo que tengo que hacer   ← lo primero, SIEMPRE
            2 · cómo voy                 (el dolor, si lo hay)
            3 · de dónde vengo           (el historial)
          «Lo que hay que hacer no puede estar debajo de nada.»

          ⚠️ Al paciente NO se le enseña el nombre técnico del cuestionario
          («vas_oficial», «SRS-22r»): no le dice nada y algunos asustan.
          Los nombres técnicos viven en NOMBRE_CUESTIONARIO, para la ficha
          clínica. Y los cuestionarios son OFICIALES: ni una palabra se toca.
          ══════════════════════════════════════════════════════════════ */}
      {tab === 'cuestionarios' && (
        <div className="fvpac fv-pagina" style={{ background: 'transparent' }}>
          <div className="fvpac__titular"><h1>Mis cuestionarios</h1></div>

          {/* 1 · LO QUE TIENE QUE HACER · lo pinta pintarPendientes() de
              FisioVisión, traída tal cual (cuestionariosHTML.js). Aquí solo se
              le pasan los datos del CRM y se engancha el botón «Responder».

              ⚠️ El prototipo enseña las PREGUNTAS y los MINUTOS de cada uno, y
              el tiempo total sumado: «si te piden tres cosas, lo primero que
              quieres saber es cuánto te va a costar». El CRM NO guarda esos dos
              datos todavía, así que llegan a 0 y esa línea sale corta. No se
              inventan: hay que guardarlos en el administrador. */}
          {pendientes.length > 0 && (
            <div
              onClick={(ev) => {
                const b = ev.target.closest('[data-resp]');
                if (!b) return;
                const q = pendientes.find((x) => String(x.id) === b.dataset.resp);
                if (q) setRespondiendo(q);
              }}
              dangerouslySetInnerHTML={{
                __html: pintarPendientesHTML(
                  pendientes.map((q) => ({
                    id: q.id,
                    nombre: q.nombre || 'Cuéntanos cómo estás',
                    preguntas: q.preguntas || 0,
                    minutos: q.minutos || 0,
                    enviado: String(q.fecha_lanzamiento || '').slice(0, 10),
                    // lo que dejó a medias · el botón cambia a «Completar»
                    empezado: !!q.empezado,
                    contestadas: q.contestadas || 0,
                    color: 'turq',
                    ico: '<rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4V3h6v1"/><path d="M9 10h6M9 14h4"/>',
                  })),
                  null,
                ),
              }}
            />
          )}

          {/* 2 · MIS RESULTADOS · la FILA de tarjetas de test, arriba del todo,
                 igual que en FisioVisión (`fv-cuest-bloques`). Ann, 28-ago-2026:
                 «Espalda (Oswestry) ponlo arriba del todo, junto a las
                 etiquetas de los otros test, como en FisioVisión».
                 La rejilla ya está clonada: cuando entren el EQ-5D y la tarjeta
                 del EVA se colocan solas al lado, sin tocar nada.
                 Clonada del prototipo (odiHTML.js). Sin ODI respondido no se
                 pinta; y con menos de 8 secciones contestadas tampoco, que lo
                 decide la propia tarjeta. */}
          {(odi.length > 0 || Object.keys(otros).length > 0) && (
            <div className="fvpac fv-cuest-bloques" style={{ background: 'transparent', marginBottom: 22 }}>
              <div dangerouslySetInnerHTML={{
                __html: odiHTML(odi, fisio ? { nombre: fisio.nombre || '' } : null),
              }} />
              {Object.keys(otros).map((t) => (
                <div key={t} dangerouslySetInnerHTML={{ __html: resultadoHTML(t, otros[t]) }} />
              ))}
            </div>
          )}

          {/* 2b · SF-12 · a todo el ancho, DEBAJO de la fila de tarjetas y
                 ENCIMA de la gráfica larga, igual que en FisioVisión.
                 El orden cuenta una historia: primero CÓMO ESTOY hoy (las
                 tarjetas) y al final DE DÓNDE VENGO (la línea).
                 Lo pinta la función de FisioVisión traída tal cual
                 (sf12HTML.js): aquí no se maqueta nada.
                 Sin SF-12 respondido no se pinta: dato real o nada. */}
          {sf12.length > 0 && (
            <div
              className="fvpac"
              style={{ background: 'transparent' }}
              dangerouslySetInnerHTML={{
                __html: sf12HTML(sf12, null, fisio ? { nombre: fisio.nombre || '' } : null),
              }}
            />
          )}

          {/* 3 · DE DÓNDE VENGO · la línea del dolor. Solo con DOS o más mediciones:
                 con una sola no hay nada que comparar y una línea de un punto
                 no dice nada. */}
          {medicionesVas.length >= 2 && (
            <section className="fv-sf" style={{ marginBottom: 22 }}>
              <div className="fv-bloque-titulo">Mi dolor, de dónde vengo</div>
              <VasEvolucionClinica mediciones={medicionesVas} />
            </section>
          )}

          {/* AQUÍ ESTABA «Lo que ya has contestado» · RETIRADO (Ann, 28-ago-2026).
              Era una lista de fechas con el nombre del cuestionario: no le
              decía nada a la paciente. Lo que sí le dice cómo va son las
              GRÁFICAS de arriba —el EVA y el SF-12—, que enseñan la evolución
              en vez de enumerar papeles. Una lista que nadie mira ocupa sitio
              y hace la pantalla más larga, nada más.

              El estado vacío se queda: si no tiene NADA, hay que decírselo. */}
          {pendientes.length === 0 && sf12.length === 0 && medicionesVas.length < 2 && (
            <div className="fv-entender-vacio">
              Aquí verás los cuestionarios que te deje tu fisio. Todavía no hay nada.
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default EspacioPacienteClinica;
export { SECCIONES };
